import { closestCenter, DndContext, DragEndEvent } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  ActionIcon,
  Button,
  CSSProperties,
  Flex,
  Group,
  NumberInput,
  Paper,
  rem,
  ScrollArea,
  Select,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { TimeInput } from "@mantine/dates";
import { useForm, zodResolver } from "@mantine/form";
import { randomId, useDebouncedCallback } from "@mantine/hooks";
import {
  IconGripVertical,
  IconMinus,
  IconPlus,
  IconSortAscending,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useBlocker } from "@tanstack/react-router";
import dayjs from "dayjs";
import { partition, sortBy } from "lodash-es";
import { z } from "zod";

import {
  getGetBookingQueryKey,
  getGetBookingsQueryKey,
  useUpdateBookingProgram,
} from "@/shared/api/generated/booking";
import { useGetProducts } from "@/shared/api/generated/products";
import {
  GetBookingData,
  GetBookingResponse,
  GetBookingsResponse,
} from "@/shared/api/generated/schemas";
import { Can } from "@/shared/providers/casl-provider";
import {
  GetFieldsBySubjectKind,
  getSubject,
} from "@/shared/providers/casl-provider/authorization/subjects";
import { useAppAbility } from "@/shared/providers/casl-provider/use-app-ability";

function useProgramFormSchema() {
  const { t } = useLingui();

  return z.object({
    products: z.array(
      z
        .object({
          id: z.string(),
          productId: z.string().min(1, { message: t`Velg et produkt` }),
          start: z.string(),
          end: z.string(),
          price: z
            .number({ invalid_type_error: t`Pris må være et tall` })
            .int(),
          quantity: z
            .number({ invalid_type_error: t`Antall må være et tall` })
            .int(),
        })
        .refine(
          (data) => {
            const start = dayjs(data.start, "HH:mm");
            const end = dayjs(data.end, "HH:mm");

            if (start.isValid() && end.isValid()) {
              return end.isAfter(start);
            }
            return true;
          },
          {
            message: t`Slutt må være etter start`,
            path: ["end"],
          },
        ),
    ),
  });
}

type ProgramFormInitial = z.input<ReturnType<typeof useProgramFormSchema>>;
type ProgramFormSubmit = z.output<ReturnType<typeof useProgramFormSchema>>;

export default function Products({ booking }: { booking: GetBookingData }) {
  const { i18n, t } = useLingui();

  const [initialProducts, initialProgram] = partition(
    booking.program,
    (b) => b.productID !== null,
  );

  const debouncedSubmit = useDebouncedCallback(() => {
    form.onSubmit(handleSubmit)();
  }, 1000);

  const programFormSchema = useProgramFormSchema();

  const form = useForm<ProgramFormInitial, () => ProgramFormSubmit>({
    mode: "controlled",
    validate: zodResolver(programFormSchema),
    initialValues: {
      products: sortBy(initialProducts, "index")?.map((product) => ({
        id: product.id,
        productId: product.productID ?? "",
        start: product.start ? dayjs(product.start).format("HH:mm") : "",
        end: product.end ? dayjs(product.end).format("HH:mm") : "",
        price: product.price,
        quantity: product.quantity,
      })) ?? [
        {
          id: randomId(),
          productId: "",
          start: "",
          end: "",
          price: 0,
          quantity: 0,
        },
      ],
    },
    onValuesChange: debouncedSubmit,
    clearInputErrorOnChange: false,
  });

  useBlocker({
    blockerFn: () =>
      window.confirm(
        t`Det er gjort endringer i skjemaet som ikke er lagret. Trykk "OK" for å forkaste endringene.`,
      ),
    condition: form.isDirty(),
  });

  const queryClient = useQueryClient();

  const { mutateAsync: updateProgram } = useUpdateBookingProgram({
    mutation: {
      onSuccess: (data) => {
        // Update entire booking itself
        queryClient.setQueryData<GetBookingResponse>(
          getGetBookingQueryKey(data.data.id),
          (prev) => ({
            ...prev,
            data: { ...prev?.data, ...data.data },
          }),
        );
        // Update this booking in the for GET /bookings
        queryClient.setQueriesData<GetBookingsResponse>(
          { queryKey: getGetBookingsQueryKey() },
          (old) => ({
            ...old,
            data: (old?.data ?? []).map((booking) =>
              booking.id === data.data.id ? data.data : booking,
            ),
          }),
        );
      },
    },
  });

  async function handleSubmit(values: ProgramFormSubmit) {
    await updateProgram({
      id: booking.id,
      data: {
        products: [
          ...initialProgram,
          ...values.products.map((product, index) => {
            let start: string | null = null;

            if (product.start) {
              const startTime = dayjs(product.start, "HH:mm");
              start = dayjs(booking.arrival)
                .hour(startTime.hour())
                .minute(startTime.minute())
                .toISOString();
            }

            let end: string | null = null;

            if (product.end) {
              const endTime = dayjs(product.end, "HH:mm");
              end = dayjs(booking.arrival)
                .hour(endTime.hour())
                .minute(endTime.minute())
                .toISOString();
            }

            return {
              id: product.id.startsWith("mantine") ? undefined : product.id,
              productId: product.productId,
              start: start,
              end: end,
              price: product.price,
              quantity: product.quantity,
              index,
            };
          }),
        ],
        updatedAt: booking.updatedAt,
      },
    });
    form.resetDirty(values);
  }

  const products = form.getValues().products;

  const totalValue = products.reduce((acc, programItem) => {
    return acc + Number(programItem.price) * programItem.quantity;
  }, 0);

  function handleDragEnd({ active, over }: DragEndEvent) {
    if (active && over && active.id !== over.id) {
      form.reorderListItem("products", {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        from: active.data.current?.sortable.index,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        to:
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          over.data.current?.sortable.index ??
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          active.data.current?.sortable.index,
      });
    }
  }

  function addNewProgramItem() {
    form.insertListItem("products", {
      id: randomId(),
      productId: "",
      start: "",
      end: "",
      price: 0,
      quantity: 0,
    } satisfies ProgramFormInitial["products"][0]);
  }

  function sortProductsByStartTime() {
    const currentProducts = form.getValues().products;
    const sorted = sortBy(currentProducts, (item) => {
      if (!item.start) return Infinity;
      return dayjs(item.start, "HH:mm").valueOf();
    });
    form.setFieldValue("products", sorted);
  }

  return (
    <DndContext
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={handleDragEnd}
    >
      <Paper p="xs" h="100%">
        <Text size="sm" mb="sm">
          <Trans>Produkter og kostnader</Trans>
        </Text>
        <Stack gap="sm">
          <ScrollArea>
            <Table
              withRowBorders={false}
              horizontalSpacing={4}
              verticalSpacing={4}
            >
              <Table.Thead>
                <Table.Tr>
                  <Can
                    I="update"
                    this={getSubject("Booking", booking)}
                    field="program"
                  >
                    <Table.Th style={{ width: rem(32) }} />
                  </Can>
                  <Table.Th style={{ minWidth: rem(150) }}>
                    <Trans>Produkt</Trans>
                  </Table.Th>
                  <Table.Th style={{ width: rem(75) }}>
                    <Trans>Start</Trans>
                  </Table.Th>
                  <Table.Th style={{ width: rem(75) }}>
                    <Trans>Slutt</Trans>
                  </Table.Th>
                  <Table.Th style={{ width: rem(110), minWidth: rem(70) }}>
                    <Trans>Pris (inkl. MVA)</Trans>
                  </Table.Th>
                  <Table.Th style={{ width: rem(90), minWidth: rem(80) }}>
                    <Trans>Antall</Trans>
                  </Table.Th>
                  <Can
                    I="update"
                    this={getSubject("Booking", booking)}
                    field="program"
                  >
                    <Table.Th style={{ width: rem(36) }} />
                  </Can>
                </Table.Tr>
              </Table.Thead>

              <Table.Tbody>
                <SortableContext
                  items={products.map(({ id }) => id)}
                  strategy={verticalListSortingStrategy}
                >
                  {products.map((product, index) => (
                    <DraggableRow
                      key={product.id}
                      id={product.id}
                      locationId={booking.location.id}
                      index={index}
                      form={form}
                      booking={booking}
                    />
                  ))}
                  {products.length === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={8}>
                        <Text c="dimmed" size="sm" ta="center" mt="sm">
                          <Trans>
                            Klikk på &quot;Legg til produkt&quot; for å legge
                            til produkter i programmet
                          </Trans>
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </SortableContext>
              </Table.Tbody>
            </Table>
          </ScrollArea>

          <Can I="update" this={getSubject("Booking", booking)} field="program">
            <Group>
              <Button
                onClick={addNewProgramItem}
                leftSection={<IconPlus size={16} />}
                size="compact-xs"
                variant="outline"
                color="gray"
              >
                <Trans>Legg til produkt</Trans>
              </Button>
              <Button
                onClick={sortProductsByStartTime}
                leftSection={<IconSortAscending size={16} />}
                size="compact-xs"
                variant="outline"
                color="gray"
              >
                <Trans>Sorter etter tid</Trans>
              </Button>
            </Group>
          </Can>

          <Flex justify="end" gap={8}>
            <Text fw="bold">
              <Trans>Total</Trans>
            </Text>
            <Text fw="bold">
              {i18n.number(totalValue, {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              })}
            </Text>
            <Text c="dimmed">NOK</Text>
          </Flex>
        </Stack>
      </Paper>
    </DndContext>
  );
}

function DraggableRow({
  id,
  index,
  locationId,
  form,
  booking,
}: {
  id: string;
  index: number;
  locationId: string;
  form: ReturnType<typeof useForm<ProgramFormInitial>>;
  booking: GetBookingData;
}) {
  const { transform, transition, setNodeRef, attributes, listeners } =
    useSortable({
      id,
    });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition,
  };

  const { data: existingProducts } = useGetProducts(
    { locations: [locationId] },
    { query: { enabled: !!locationId } },
  );

  /**
   * Sets the end time of the product based on the start time and the duration of the product.
   * @param startTimeInput expected format: "HH:mm"
   */
  function setProductEndTime(startTimeInput: string) {
    const startTime = dayjs(startTimeInput, "HH:mm");

    if (startTime.isValid()) {
      const selectedProductId = form.getValues().products[index].productId;

      if (selectedProductId) {
        const product = existingProducts?.data?.find(
          (product) => product.id === selectedProductId,
        );

        if (product?.duration) {
          const endTime = startTime.add(product.duration, "minutes");

          form.setFieldValue(`products.${index}.end`, endTime.format("HH:mm"));
        }
      }
    }
  }

  const onStartTimeChange =
    (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      // onChange is typed as any
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      form.getInputProps(`products.${index}.start`).onChange(e);
      setProductEndTime(e.target.value);
    };

  const productOptions = existingProducts?.data?.map((product) => ({
    value: product.id,
    label: product.nameNO,
  }));

  function setProductPrice(selectedProductId: string | null) {
    if (selectedProductId) {
      const product = existingProducts?.data?.find(
        (product) => product.id === selectedProductId,
      );

      // Set prica automatically if product has price
      if (product?.price) {
        form.setFieldValue(`products.${index}.price`, product.price);
      }

      // Set amount to 1 if it's 0
      if (form.getValues().products[index].quantity === 0) {
        form.setFieldValue(`products.${index}.quantity`, 1);
      }
    } else {
      // Set price to 0 if product is removed
      form.setFieldValue(`products.${index}.price`, 0);
    }
  }

  function maybeResetProductStartEnd(selectedProductId: string | null) {
    if (selectedProductId) {
      const product = existingProducts?.data?.find(
        (product) => product.id === selectedProductId,
      );

      // if selected product has no duration, reset start and end
      if (!product?.duration) {
        form.setFieldValue(`products.${index}.start`, "");
        form.setFieldValue(`products.${index}.end`, "");
      }
    }
  }

  const onProductChange =
    (index: number) => (selectedProductId: string | null) => {
      // onChange is typed as any
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      form
        .getInputProps(`products.${index}.productId`)
        .onChange(selectedProductId);
      setProductPrice(selectedProductId);
      maybeResetProductStartEnd(selectedProductId);
    };

  const ability = useAppAbility();

  const canUpdateBookingProgram = (field: GetFieldsBySubjectKind<"Booking">) =>
    ability.can("update", getSubject("Booking", booking), field);

  return (
    <Table.Tr ref={setNodeRef} style={style}>
      <Can
        I="update"
        this={getSubject("Booking", booking)}
        field={"program.index" satisfies GetFieldsBySubjectKind<"Booking">}
      >
        <Table.Td {...attributes} {...listeners} valign="top">
          <IconGripVertical style={{ marginTop: 6 }} />
        </Table.Td>
      </Can>
      <Table.Td valign="top">
        <Select
          data={sortBy(productOptions, (o) => o.label.toLowerCase())}
          searchable
          clearable
          {...form.getInputProps(`products.${index}.productId`)}
          onChange={onProductChange(index)}
          key={form.key(`products.${index}.productId`)}
          readOnly={!canUpdateBookingProgram("program.productID")}
        />
      </Table.Td>
      <Table.Td valign="top">
        <TimeInput
          {...form.getInputProps(`products.${index}.start`)}
          key={form.key(`products.${index}.start`)}
          onChange={onStartTimeChange(index)}
          readOnly={!canUpdateBookingProgram("program.start")}
        />
      </Table.Td>
      <Table.Td valign="top">
        <TimeInput
          {...form.getInputProps(`products.${index}.end`)}
          key={form.key(`products.${index}.end`)}
          readOnly={!canUpdateBookingProgram("program.end")}
        />
      </Table.Td>
      <Table.Td valign="top">
        <NumberInput
          {...form.getInputProps(`products.${index}.price`)}
          key={form.key(`products.${index}.price`)}
          hideControls
          allowDecimal={false}
          allowNegative={false}
          thousandSeparator={" "}
          styles={{
            input: { textAlign: "right" },
          }}
          readOnly={!canUpdateBookingProgram("program.price")}
        />
      </Table.Td>
      <Table.Td valign="top">
        <NumberInput
          {...form.getInputProps(`products.${index}.quantity`)}
          key={form.key(`products.${index}.quantity`)}
          readOnly={!canUpdateBookingProgram("program.quantity")}
        />
      </Table.Td>
      <Can
        I="update"
        this={getSubject("Booking", booking)}
        field={"program" satisfies GetFieldsBySubjectKind<"Booking">}
      >
        <Table.Td valign="top">
          <ActionIcon
            onClick={() => {
              form.removeListItem("products", index);
            }}
            variant="outline"
            color="red"
            mt={4}
          >
            <IconMinus />
          </ActionIcon>
        </Table.Td>
      </Can>
    </Table.Tr>
  );
}
